<script setup>
import { ref, computed, onMounted } from 'vue'

import { Dropdown, Icon, ProfilePicture } from '@components'

import { useRoute } from 'vue-router'
const route = useRoute()

import { useAppointment } from '@store/appointment'
const $appointment = useAppointment()

import { useStaff } from '@store/staff'
const $staff = useStaff()

import { useService } from '@store/service'
const $service = useService()


import { useTransaction } from '@store/transaction'
const $transaction = useTransaction()

import { useSettings } from '@store/settings'
const $settings = useSettings()

import { getPrice } from '@utils'
import moment from 'moment'

const props = defineProps({
    appointmentId: {
        type: [Number, String],
        required: true,
        default: () => null
    }
})

const emit = defineEmits(['close'])

const appointment = computed(() => {
    if (!props.appointmentId) return {}

    return $appointment.getById(props.appointmentId)
})

const staff = computed(() => {
    if (!props.appointmentId || !appointment.value || !appointment.value.staff_id) return {}

    return $staff.getStaffById(appointment.value.staff_id)
})

const service = computed(() => {
    if (!props.appointmentId || !appointment.value || !appointment.value.service_id) return {}

    return $service.getServiceById(appointment.value.service_id)
})

onMounted(async () => {
    document.addEventListener('click', (event) => {
        if (!event.target.closest('.prebook-appointment-details-modal')) emit('close')
    })
})

</script>
<template>
    <div class="prebook-appointment-details-modal w-[300px] overflow-y-auto shadow-md z-[150] absolute bg-white" v-bind="$attrs">
        <div class="p-5 rounded-[8px]">
            <div class="flex items-center justify-between gap-2">
                <div class="text-[20px] font-bold text-[#101828]">{{ appointment.meta?.service_name }}</div>
                <div class="flex gap-2">
                    <Dropdown :contentClass="`w-56 top-[30px] w-[117px] z-[9] ${$settings.isRTL ? 'left-[-32px]' : 'right-0'}`">
                        <Icon name="dotsVertical" class="text-[#98A2B3] cursor-pointer"></Icon>
                        <template #content>
                            <div class="flex flex-col gap-1">
                                <div class="rounded-md mx-1.5 my-0.5 transition">
                                    <router-link :to="{ name: 'appointment.edit', params: { appointmentId: appointment.appointment_id }, query: { backTo: route.fullPath } }" class="px-2.5 py-[9px] flex gap-2 items-center transition-all hover:bg-[#F9FAFB] rounded-md cursor-pointer">
                                        <Icon size="16px" name="edit05" />
                                        <div class="text-[#344054] text-sm font-medium">Edit</div>
                                    </router-link>
                                </div>
                            </div>
                        </template>
                    </Dropdown>
                    <Icon name="xClose" class="cursor-pointer text-[#98A2B3]" @click="emit('close')"></Icon>
                </div>
            </div>
            <div class="flex gap-2 items-center text-[#667085] text-sm mt-1">
                <!-- <div>{{ appointment.meta?.service_duration || $service.getServiceById(appointment.service_id)?.meta.duration }} {{ appointment.meta?.service_duration_unit || $service.getServiceById(appointment.service_id)?.meta.duration_unit }}</div> -->
                <div>{{ service?.meta?.duration }} {{ service?.meta?.duration_unit }}{{ service?.meta?.duration > 1 && !service?.meta?.duration_unit.endsWith('s') ? 's' : '' }}</div>
                <Icon v-if="service.meta.price_type != 'none'" name="ellipse01" size="4px" class="cursor-pointer text-[#98A2B3]"></Icon>
                <div v-if="service.meta.price_type != 'none'">{{ service.meta.price_type == 'free' ? 'Free' : getPrice(appointment.meta?.price) }}</div>
            </div>
            <div class="flex items-center gap-2 mt-3">
                <div v-if="$settings.addons.staffs && staff" class="flex items-center gap-[6px] p-[2px_8px_2px_3px] rounded-2xl bg-[#F2F4F7]">
                    <!-- <div class="w-4 h-4 rounded-full" :style="`background-image:url(${$staff.getStaffById(appointment.staff_id)?.avatar})`"></div> -->
                    <ProfilePicture :name="staff?.name" :img="staff?.meta?.profile_image_url" :bgColor="staff?.meta?.color" class="w-4 h-4" textClass="!text-[10px]" />
                    <div class="text-[#344054] text-sm font-medium">{{ staff?.name }}</div>
                </div>
                <div class="text-sm font-medium p-[2px_8px] rounded-2xl w-fit capitalize" v-if="$settings.addons.payment && service.meta.price_type == 'fixed'" :class="{
                    'text-[#027A48] bg-[#ECFDF3]': appointment.is_paid,
                    'text-[#B84646] bg-[#FEE2E2]': !appointment.is_paid
                }"> {{ appointment.is_paid ? 'Paid' : 'Unpaid' }} </div>
                <!-- <div class="text-[#B54708] text-sm font-medium p-[2px_8px] rounded-2xl bg-[#FFFAEB] w-fit">Due: $150.00</div> -->
            </div>
        </div>
        <div class="w-full h-1 bg-[#F2F4F7]">
            <!-- <div class="w-1/2 h-1 bg-[#9E77ED]"></div> -->
        </div>
        <div class="p-5">
            <div class="flex gap-[15px] mt-[15px] first:mt-0">
                <Icon name="clock" size="24px" class="text-[#667085]"></Icon>
                <div class="w-[calc(100%-40px)]">
                    <div class="text-[#101828] text-base font-semibold">{{ moment(appointment.booking_date,
                        'YYYY-MM-DD').format('dddd, MMMM DD') }}</div>
                    <div class="text-[#667085] text-sm font-medium mt-1">{{ moment(appointment.booking_time_start,
                        'HH:mm').format('h:mm A') }} - {{ moment(appointment.booking_time_end, 'HH:mm').format('h:mm A')
                        }} </div>
                </div>
            </div>
            <div class="flex gap-[15px] mt-[15px] first:mt-0">
                <Icon name="user03" size="24px" class="text-[#667085]"></Icon>
                <div class="w-[calc(100%-40px)]">
                    <div class="text-[#101828] text-base font-semibold">{{ appointment.meta?.customer_first_name }} {{
                        appointment.meta?.customer_last_name }}</div>
                    <div class="text-[#667085] text-sm font-medium mt-1">{{ appointment.meta?.customer_email }}</div>
                    <div class="text-[#667085] text-sm font-medium mt-1">{{ appointment.meta?.customer_phone }}</div>
                </div>
            </div>
            <div class="flex gap-[15px] mt-[15px] first:mt-0" v-if="appointment.meta?.address">
                <Icon name="map02" size="24px" class="text-[#667085]"></Icon>
                <div class="w-[calc(100%-40px)]">
                    <div class="text-[#101828] text-base font-semibold">Address</div>
                    <div class="text-[#667085] text-sm font-medium mt-1">{{ appointment.meta?.address }}</div>
                </div>
            </div>
            <div class="flex gap-[15px] mt-[15px] first:mt-0" v-if="appointment.meta?.customer_note">
                <Icon name="notificationText" size="24px" class="text-[#667085]"></Icon>
                <div class="w-[calc(100%-40px)]">
                    <div class="text-[#101828] text-base font-semibold">Note</div>
                    <div class="text-[#667085] text-sm font-medium mt-1">{{ appointment.meta?.customer_note }}</div>
                </div>
            </div>
        </div>
    </div>
</template>
<style>
.prebook-appointment-details-modal {
    animation: detailsOpen 0.1s ease-in-out;
}

@keyframes detailsOpen {
    0% {
        transform: scale(0.7);
        opacity: 0;
    }

    100% {
        transform: scale(1);
    }
}
</style>